import { Component } from '@angular/core';
import { MessageService } from 'primeng/api';
import { ApiService } from '../services/api.service';

export interface UploadResult {
  total_rows: number;
  inserted: number;
  skipped_yard: number;
  skipped_duplicate: number;
  errors: number;
  error_details: string[];
}

@Component({
  selector: 'app-upload',
  templateUrl: './upload.component.html',
  styleUrls: ['./upload.component.scss'],
})
export class UploadComponent {
  selectedFile: File | null = null;
  isDragging = false;
  isUploading = false;
  uploadResult: UploadResult | null = null;

  constructor(
    private apiService: ApiService,
    private messageService: MessageService
  ) {}

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.isDragging = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.isDragging = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.isDragging = false;
    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.setFile(files[0]);
    }
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.setFile(input.files[0]);
    }
  }

  private setFile(file: File): void {
    const name = file.name.toLowerCase();
    if (!name.endsWith('.xlsx') && !name.endsWith('.xls')) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Invalid File',
        detail: 'Only .xlsx and .xls files are accepted.',
      });
      return;
    }
    this.selectedFile = file;
    this.uploadResult = null;
  }

  formatBytes(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  clearFile(): void {
    this.selectedFile = null;
    this.uploadResult = null;
  }

  onUpload(): void {
    if (!this.selectedFile) return;

    const formData = new FormData();
    formData.append('file', this.selectedFile, this.selectedFile.name);

    this.isUploading = true;
    this.uploadResult = null;

    this.apiService.postFormData<UploadResult>('/api/upload', formData).subscribe({
      next: (result) => {
        this.uploadResult = result;
        this.isUploading = false;

        if (result.inserted > 0) {
          this.messageService.add({
            severity: 'success',
            summary: 'Upload Complete',
            detail: `${result.inserted} record${result.inserted !== 1 ? 's' : ''} imported successfully.`,
          });
        } else {
          this.messageService.add({
            severity: 'info',
            summary: 'Upload Complete',
            detail: 'No new records were imported.',
          });
        }
      },
      error: (err) => {
        this.isUploading = false;
        const detail =
          err?.error?.detail || 'Failed to process the file. Please check the format and try again.';
        this.messageService.add({
          severity: 'error',
          summary: 'Upload Failed',
          detail,
        });
      },
    });
  }
}
